import { FloatingLabelInput } from '@/components/ui/input';
import { useForm } from '@tanstack/react-form';
import { Button } from '@/components/ui/button';
import { useMutation } from '@tanstack/react-query';
import { loginWithCustomerId } from '@/services/apis/auth/loginWithCustomerId';
import { CUSTOMER_ID_FORM } from '@/constants/auth.constants';
import { apiMutationKeys } from '@/services/apis/apiMutationKeys';
import { useGlobalStore } from '@/stores/store';
import { FieldError } from '@/components/ui/form-field-error';
import { replaceNonDigit } from '@/utils/string';
import { hasFieldErrors } from '@/utils/forms';
import { PRIMARY_PHONE_NUMBER_UNFORMATTED } from '@/constants/global.constants';
import { z } from 'zod';
import { getGlobalStore } from '@/utils/store';

const CustomerIdSchema = z.object({
  customerId: z
    .string()
    .min(1, { message: 'Please enter a valid Customer ID' }),
});

const CustomerIdForm = () => {
  const email = useGlobalStore((store) => store.authStore.authFlow.email);
  const { setAuthFlowCurrentStep, setInitialAccessToken, setCustomerId } =
    getGlobalStore();
  const handleSubmit = async ({ value }: { value: { customerId: string } }) => {
    try {
      await mutateAsync(
        { customerId: value.customerId, email },
        {
          onSuccess: (data) => {
            setInitialAccessToken(data.access_token);
            setCustomerId(value.customerId);
          },
        }
      );
      setAuthFlowCurrentStep('verifyAccount');
    } catch (error) {
      // Propagate error from API to the form (No matter what the error is)
      form.setFieldMeta('customerId', (prevMeta) => ({
        ...prevMeta,
        errorMap: {
          onChange:
            (error as Error)?.message ||
            CUSTOMER_ID_FORM.FALLBACK_LOGIN_ERROR_MESSAGE,
        },
      }));
    }
  };
  const { mutateAsync, isPending, isError } = useMutation({
    mutationFn: loginWithCustomerId,
    mutationKey: [apiMutationKeys.basicLogin],
  });
  const form = useForm({
    defaultValues: {
      customerId: '',
    },
    validators: {
      onChange: CustomerIdSchema,
    },
    onSubmit: handleSubmit,
  });

  const handleOnClickCallUs = () => {
    window.location.href = `tel:${PRIMARY_PHONE_NUMBER_UNFORMATTED}`;
  };

  return (
    <div className="flex flex-col gap-6 text-grey-900">
      <p className="text-xl font-nunito700">{CUSTOMER_ID_FORM.TITLE}</p>
      <p className="text-base">{CUSTOMER_ID_FORM.SUBTITLE}</p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <form.Field name="customerId">
          {(field) => (
            <div className="flex flex-col gap-1">
              <FloatingLabelInput
                maxLength={20}
                name="customerId"
                value={field.state.value}
                onChange={(e) =>
                  field.handleChange(replaceNonDigit(e.target.value))
                }
                label="Customer ID"
                size="large"
                hasError={isError || hasFieldErrors(field)}
              />
              <FieldError field={field} />
            </div>
          )}
        </form.Field>
        <div className="flex flex-col gap-6">
          <p className="text-sm text-grey-600">
            {CUSTOMER_ID_FORM.HINT_MESSAGE}
          </p>
          <div className="block space-y-6 lg:space-y-0 lg:flex lg:flex-row lg:gap-4">
            <form.Subscribe
              selector={(state) => [state.canSubmit]}
              children={([canSubmit]) => (
                <Button
                  variant="primary"
                  type="submit"
                  size="medium"
                  disabled={!canSubmit}
                  loading={isPending}
                  className="flex-none"
                >
                  {CUSTOMER_ID_FORM.SUBMIT_BUTTON}
                </Button>
              )}
            />
            {isError && (
              <Button
                onClick={handleOnClickCallUs}
                variant="secondary"
                size="medium"
                className="flex-none"
              >
                {CUSTOMER_ID_FORM.CALL_US_BUTTON}
              </Button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
};
export { CustomerIdForm };
